/*
 * Confidential and Proprietary.
 * Do not distribute without 1-800-Flowers.com, Inc. consent.
 * Copyright 1-800-Flowers.com, Inc. 2019. All rights reserved.
 */

import { useState } from 'react';
import mbpLogger from '@module-logger/mbp';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation } from 'react-router';

import {
    setDialogOpen, setDialogType, setOfferGatewayCTALabel, setOfferGatewayData,
    setOGAutoScroll,
} from '../../../../../../state/ducks/App/App-Actions';
import { useAuth } from '../../../../../../state/ducks/Member/ducks/Auth/Plugin/auth';
import { emitCustomTrackingEvent } from '../../../../../../state/ducks/TagManager/ducks/ClickStreamEvents/ClickStreamEvent-Actions';
import { getPageType } from '../../../../../../state/ducks/App/App-Selectors';

function useOfferGatewayClickHandler(
    salesforceResponse, offer,
) {
    const [isPromoCopied, setIsPromoCopied] = useState(false);
    const dispatch = useDispatch();
    const location = useLocation();
    const { loginByRedirect } = useAuth();
    const pageType = useSelector(getPageType);

    const offerDetails = salesforceResponse || offer;
    const experimentTracking = {
        campaignId: offer?.value?.featureId,
        campaignName: offer?.value?.featureId,
        id: offer?.value?.key,
        name: offer?.value?.name,
        source: 'growthbook',
        variant: offer?.value?.variationId,
    };

    const campaignPayload = salesforceResponse ? salesforceResponse?.campaign?.campaignResponses?.[0]?.payload || {} : offer?.value?.value?.offerGatewayRecipe || {};

    const isOfferUnlocked = campaignPayload.offerFlow === 'Offer Unlocked' || campaignPayload.offerFlow === 'Offer Unlocked - SMS';
    const isAuthFlow = campaignPayload?.offerFlow === 'Login' || campaignPayload?.offerFlow === 'Account Creation';

    const handleAuthRoute = async () => {
        if (campaignPayload?.offerFlow === 'Login') {
            await loginByRedirect({ 'ext-offer-gateway-flow': 'yes' }, { routeBack: location.pathname, register: 'N' });
        } else if (campaignPayload?.offerFlow === 'Account Creation') {
            await loginByRedirect({ 'ext-offer-gateway-flow': 'yes' }, { routeBack: location.pathname, register: 'Y' });
        }
    };

    const copyPromoCode = async (textToCopy) => {
        try {
            if (typeof navigator !== 'undefined' && navigator.clipboard) {
                await navigator.clipboard.writeText(textToCopy);
                setIsPromoCopied(true);
            } else if (typeof document !== 'undefined') {
                // for old browsers
                const textArea = document.createElement('textarea');
                textArea.value = textToCopy;
                textArea.style.position = 'fixed';
                textArea.style.opacity = '0';
                document.body.appendChild(textArea);
                textArea.select();
                document.execCommand('copy'); // depricated
                document.body.removeChild(textArea);
                setIsPromoCopied(true);
            }
        } catch (error) {
            mbpLogger.logError({
                function: 'OfferGatewayMessageStandard',
                message: 'Error while coping promo in offer gateway',
                appName: process.env.npm_package_name,
                module: 'Offer Gateway',
                jsError: error,
            });
        }
    };

    const handleOfferFlow = (button) => {
        if (!isOfferUnlocked) {
            dispatch(emitCustomTrackingEvent({
                action: 'Click',
                eventSubType: 'campaign-tracking',
                page: { type: pageType || 'home' },
                salesforceResponse: offerDetails,
                experimentTracking,
            }));
            dispatch(setOfferGatewayData({
                salesforceResponse,
            }));

            // Handle login or account creation flow
            handleAuthRoute();
        } else if (button?.offer_status === 'copy') {
            copyPromoCode(campaignPayload?.offerBannerCode);
        }
    };

    const handlePhoneSuccessClick = (event) => {
        event?.stopPropagation();

        if (!isAuthFlow) {
            event?.preventDefault();
            dispatch(setDialogOpen(false));
            dispatch(setDialogType('offerGateway'));
        }
    };

    const handleButtonClick = ({ event, item }) => {
        event?.stopPropagation();

        if (!isAuthFlow && item?.cta_button?.launch_modal) {
            // avoid launching modal if user is in auth flow
            event?.preventDefault();
            dispatch(setDialogOpen(true));
            dispatch(setDialogType('offerGateway'));
            dispatch(setOfferGatewayCTALabel(item?.cta_button?.tracking?.tracking_event_label));
            dispatch(setOGAutoScroll(true));
        }

        handleOfferFlow(item);
    };

    const handleSiteWideBannerClick = (item) => {
        if (isOfferUnlocked) {
            dispatch(emitCustomTrackingEvent({
                action: 'Click',
                eventSubType: 'campaign-tracking',
                page: { type: pageType || 'home' },
                salesforceResponse: offerDetails,
                experimentTracking,
            }));
        }
        if (!isOfferUnlocked && !isAuthFlow && offerDetails?.content?.custom_button?.[0]?.cta_button?.launch_modal) {
            dispatch(setDialogOpen(true));
            dispatch(setDialogType('offerGateway'));
            dispatch(setOGAutoScroll(false));
        }
        handleOfferFlow(item);
    };

    const handleBannerClick = () => {
        if (isOfferUnlocked) return;

        if (!isAuthFlow && offerDetails?.content?.message?.custom_button?.[0]?.cta_button?.launch_modal) {
            dispatch(setDialogOpen(true));
            dispatch(setDialogType('offerGateway'));
            dispatch(setOfferGatewayCTALabel(offerDetails.content.message.custom_button[0].cta_button.tracking?.tracking_event_label));
            dispatch(setOGAutoScroll(true));
        }

        handleOfferFlow();
    };

    const handleOfferInboxClick = () => {
        dispatch(emitCustomTrackingEvent({
            action: 'Click',
            eventSubType: 'campaign-tracking',
            page: { type: pageType || 'home' },
            salesforceResponse: offerDetails,
            experimentTracking,
        }));

        if (!isOfferUnlocked) {
            // Handle login or account creation flow
            handleAuthRoute();
        }
    };

    return {
        handleBannerClick, handleButtonClick, isPromoCopied, handleSiteWideBannerClick, handlePhoneSuccessClick, handleOfferInboxClick,
    };
}

export default useOfferGatewayClickHandler;
